<script lang="ts" setup>
import type { TReportCard } from '~/src/types/reports'

import { logError } from '~/utils/log-error'
import TheLoader from '~/components/TheLoader.vue'
import AssessmentsReportList from '~/components/assessments/ReportList.vue'

import { MARKET_TYPE } from '~/src/constants/markets'
import { COPPER, PRICE_ASSESSMENTS } from '~/constants/services/constants'
import { useSeo } from '~/composables/useSeo'
import { sortByTitleReport } from '~/src/utils/reports/sort'
import { setReportImages } from '~/src/utils/reports/image'
import { filterReports } from '~/src/utils/reports/filter'

definePageMeta({ scrollToTop: false })

const reports = ref<TReportCard[]>([])
const loading = ref(true)
const initialFields = [PRICE_ASSESSMENTS.slug, COPPER.slug]

const { $downloadManager } = useNuxtApp()
const authStore = useAuthStore()
const authenticated = computed(() => authStore.authenticated)

const getReports = async () => {
  loading.value = true
  try {
    const data = await $downloadManager.getAllReports({
      market: COPPER.slug,
      marketTypes: [MARKET_TYPE.WEEKLY_REPORTS],
      show_total: true,
      page_limit: -1,
    })
    const files = filterReports(data, 'forecast-video-intro')
    reports.value = sortByTitleReport(files.noMatch)
    reports.value = setReportImages(reports.value, COPPER.slug)
    reports.value = reports.value.map((report) => ({
      ...report,
      assessment_type: null,
    }))
  } catch (error) {
    logError(error)
  } finally {
    loading.value = false
  }
}
const { getSeoInfo } = useSeo()
const seo = getSeoInfo({
  title: 'Copper Briefing',
  description:
    "Benchmark’s Copper Briefing provides exclusive analysis on copper submarkets, price trends, and copper's evolving role in the energy transition.",
})

useHead(seo)

onMounted(async () => {
  await getReports()
})

watch(authenticated, async (value) => {
  if (!value) return
  await getReports()
})
</script>

<template>
  <div class="relative min-h-[500px]">
    <div v-if="loading" class="absolute left-0 w-full lg:top-1/2">
      <TheLoader />
    </div>

    <AssessmentsReportList
      v-else
      :reports="reports"
      :category="COPPER.categoryReportPricing"
      :initial-fields="initialFields"
      :show-latest-report-section="false"
      class="pt-6"
    />
  </div>
</template>
